import { Button, Flex, Input, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FormModal } from '../../../components/FormModal'
import { TableSkeletonRows } from '../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { useAppToast } from '../../../helpers'
import { ICourseThemeColor } from '../../../services/ScheduleClasses/interfaces'
import ScheduleClassService from '../../../services/ScheduleClasses/ScheduleClassService'
import { DEFAULT_THEME_COLOR } from './constants'

interface IProps {
  isOpen: boolean
  onClose: () => void
  canEdit: boolean
}

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/

/**
 * Cores dos cursos coletivos usadas pelo cronograma no site (ABRA-181).
 *
 * Salvamento é por linha, sem botão global. O PATCH acontece no onBlur e nunca no onChange:
 * o seletor nativo de cor dispara onChange a cada movimento do ponteiro enquanto o usuário
 * arrasta, o que geraria dezenas de requisições por escolha.
 */
export default function CourseThemeColorsModal({ isOpen, onClose, canEdit }: IProps) {
  const toast = useAppToast()
  const service = useMemo(() => new ScheduleClassService(), [])
  const [courses, setCourses] = useState<ICourseThemeColor[]>([])
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [savingId, setSavingId] = useState<number | null>(null)

  /**
   * Cor que já está sendo gravada, por curso. Ref e não state porque a segunda chamada acontece
   * antes de qualquer re-render: escolher a cor no seletor e clicar direto no campo hexadecimal
   * dispara o onBlur do seletor, que desabilita os dois inputs — e desabilitar um input focado
   * dispara blur nele, chamando save de novo. O guard por `course.theme_color` não pega esse
   * caso porque `course` ainda reflete o valor antigo nas duas chamadas.
   */
  const savingColor = useRef<Record<number, string | null>>({})

  const fetchCourses = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      setCourses(await service.listThemeColors())
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as cores dos cursos', error)
    } finally {
      setLoading(false)
    }
  }, [service, toast])

  useEffect(() => {
    if (!isOpen) return
    setDrafts({})
    fetchCourses()
  }, [isOpen, fetchCourses])

  async function save(course: ICourseThemeColor, color: string | null) {
    if (color !== null && !HEX_PATTERN.test(color)) {
      toast.error('Informe uma cor no formato #RRGGBB')
      setDrafts((d) => ({ ...d, [course.id]: course.theme_color ?? DEFAULT_THEME_COLOR }))
      return
    }

    /**
     * A cor padrão nunca é gravada: é exatamente o que o curso já exibe com theme_color nulo.
     *
     * Sem isto, valueOf() devolvia o padrão para curso sem cor própria e o onBlur o gravava
     * como escolha explícita — bastava clicar no seletor e sair, sem trocar cor nenhuma. Nada
     * mudava na tela, mas o curso passava a ter magenta "por decisão": o botão Padrão
     * habilitava e ele deixava de acompanhar uma futura troca da cor de marca.
     *
     * Comparação sem caixa porque o cadastro de cursos (PUT /courses/{id}) aceita o
     * hexadecimal como vier; só este endpoint normaliza para maiúsculas.
     */
    const nextColor = color?.toUpperCase() === DEFAULT_THEME_COLOR ? null : color

    if ((course.theme_color ?? null) === nextColor) return
    if (savingColor.current[course.id] === nextColor) return

    savingColor.current[course.id] = nextColor
    setSavingId(course.id)
    try {
      const updated = await service.updateThemeColor(course.id, nextColor)
      setCourses((list) => list.map((c) => (c.id === course.id ? { ...c, theme_color: updated.theme_color } : c)))
      setDrafts((d) => {
        const next = { ...d }
        delete next[course.id]
        return next
      })
      toast.success(`Cor de ${course.name} atualizada.`)
    } catch {
      toast.error(`Erro ao salvar a cor de ${course.name}`)
      setDrafts((d) => ({ ...d, [course.id]: course.theme_color ?? DEFAULT_THEME_COLOR }))
    } finally {
      delete savingColor.current[course.id]
      setSavingId(null)
    }
  }

  function valueOf(course: ICourseThemeColor): string {
    return drafts[course.id] ?? course.theme_color ?? DEFAULT_THEME_COLOR
  }

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Cores dos cursos" size="compact">
      <Text variant="caption" mb={4}>
        Define a cor usada pelo cronograma no site. Cursos sem cor própria usam o padrão {DEFAULT_THEME_COLOR}.
      </Text>

      {/* Sem TableContainer de propósito: ele aplica white-space: nowrap, e nome de curso
          longo ("Desenho de Observação e Perspectiva") esticava a tabela além do modal,
          deixando uma barra de rolagem horizontal no rodapé. Aqui o nome quebra em duas
          linhas — a única coluna elástica é a primeira, as outras duas têm largura fixa. */}
      <Table>
        <Thead>
          <Tr>
            <Th>Curso</Th>
            <Th>Cor</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <TableSkeletonRows cols={3} />
          ) : loadError ? (
            <TableErrorRow colSpan={3} entity="cursos coletivos" onRetry={fetchCourses} />
          ) : courses.length === 0 ? (
            <TableEmptyRow colSpan={3} entity="curso coletivo" />
          ) : (
            courses.map((course) => (
              <Tr key={course.id}>
                <Td>{course.name}</Td>
                {/* w="1%" encolhe a coluna até o conteúdo: os dois inputs têm largura fixa,
                    e sobra tudo o que resta para o nome do curso quebrar em vez de esticar. */}
                <Td w="1%">
                  <Flex align="center" gap={2}>
                    <Input
                      type="color"
                      aria-label={`Cor de ${course.name}`}
                      value={valueOf(course)}
                      isDisabled={!canEdit || savingId === course.id}
                      p={0}
                      w="2.5rem"
                      h="2.5rem"
                      minW="2.5rem"
                      borderWidth="1px"
                      borderRadius="md"
                      onChange={(e) => setDrafts((d) => ({ ...d, [course.id]: e.target.value.toUpperCase() }))}
                      onBlur={() => save(course, valueOf(course))}
                    />
                    <Input
                      size="sm"
                      w="110px"
                      minW="110px"
                      aria-label={`Hexadecimal da cor de ${course.name}`}
                      value={valueOf(course)}
                      isDisabled={!canEdit || savingId === course.id}
                      onChange={(e) => setDrafts((d) => ({ ...d, [course.id]: e.target.value.toUpperCase() }))}
                      onBlur={() => save(course, valueOf(course))}
                    />
                    {savingId === course.id && <Spinner size="xs" />}
                  </Flex>
                </Td>
                <Td w="1%">
                  <Tooltip label={`Voltar para o padrão ${DEFAULT_THEME_COLOR}`}>
                    <Button
                      size="xs"
                      variant="outline"
                      isDisabled={!canEdit || savingId === course.id || course.theme_color === null}
                      onClick={() => save(course, null)}
                    >
                      Padrão
                    </Button>
                  </Tooltip>
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    </FormModal>
  )
}
